<template>
  <div class="relative w-full h-full">
    <div
      v-if="!apiKey"
      class="absolute inset-0 flex items-center justify-center bg-gray-50 border border-dashed border-gray-300 rounded"
    >
      <p class="text-sm text-gray-500 px-4 text-center">
        HERE API key mancante. Configura una chiave HERE attiva nella sezione API Keys del cliente.
      </p>
    </div>

    <div
      v-else-if="loadError"
      class="absolute inset-0 flex items-center justify-center bg-red-50 border border-red-200 rounded"
    >
      <p class="text-sm text-red-600 px-4 text-center">{{ loadError }}</p>
    </div>

    <div ref="mapContainer" class="w-full h-full min-h-[400px]"></div>
  </div>
</template>

<script>
let hereScriptsPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Impossibile caricare ${src}`));
    document.head.appendChild(script);
  });
}

function loadHereSdk() {
  if (window.H && window.H.service && window.H.map) {
    return Promise.resolve();
  }

  if (hereScriptsPromise) return hereScriptsPromise;

  if (!document.getElementById('here-maps-ui-css')) {
    const link = document.createElement('link');
    link.id = 'here-maps-ui-css';
    link.rel = 'stylesheet';
    link.href = 'https://js.api.here.com/v3/3.1/mapsjs-ui.css';
    document.head.appendChild(link);
  }

  hereScriptsPromise = loadScript('https://js.api.here.com/v3/3.1/mapsjs-core.js')
    .then(() => loadScript('https://js.api.here.com/v3/3.1/mapsjs-service.js'))
    .then(() => loadScript('https://js.api.here.com/v3/3.1/mapsjs-mapevents.js'))
    .then(() => loadScript('https://js.api.here.com/v3/3.1/mapsjs-ui.js'));

  return hereScriptsPromise;
}

export default {
  name: 'RouteEditorMapHere',

  props: {
    apiKey: {
      type: String,
      default: ''
    },
    strategy: {
      type: Object,
      default: null
    },
    vehicle: {
      type: Object,
      default: null
    },
    waypoints: {
      type: Array,
      default: () => []
    },
    center: {
      type: Object,
      default: () => ({ lat: 45.0732, lng: 7.6827 })
    },
    zoom: {
      type: Number,
      default: 12
    },
    avoidTolls: {
      type: Boolean,
      default: false
    },
    avoidHighways: {
      type: Boolean,
      default: false
    },
    avoidFerries: {
      type: Boolean,
      default: false
    },
    optimizeRoute: {
      type: Boolean,
      default: false
    }
  },

  emits: ['ready', 'route-calculated', 'route-error', 'waypoints-optimized', 'adjacent-duplicates-detected'],

  data() {
    return {
      loadError: '',
      _routeRequestId: 0
    };
  },

  watch: {
    apiKey: {
      immediate: true,
      handler(newKey) {
        if (!newKey || this._map) return;

        loadHereSdk()
          .then(() => this.initMap())
          .catch(err => {
            console.error(err);
            this.loadError = err.message;
          });
      }
    },

    waypoints: {
      deep: true,
      handler() {
        this.refresh();
      }
    },
    vehicle() {
      this.refresh();
    },
    avoidTolls() {
      this.refresh();
    },
    avoidHighways() {
      this.refresh();
    },
    avoidFerries() {
      this.refresh();
    }
  },

  beforeUnmount() {
    this._routeRequestId++;
    window.removeEventListener('resize', this.resizeMap);

    if (this._routeRequestTimeout) {
      clearTimeout(this._routeRequestTimeout);
      this._routeRequestTimeout = null;
    }

    if (this._map) {
      this._map.dispose();
    }

    this._platform = null;
    this._map = null;
    this._ui = null;
    this._behavior = null;
    this._routeGroup = null;
    this._markerGroup = null;
  },

  methods: {
    initMap() {
      if (!this.$refs.mapContainer || !this.apiKey) return;

      this._platform = new window.H.service.Platform({
        apikey: this.apiKey
      });

      const defaultLayers = this._platform.createDefaultLayers();

      this._map = new window.H.Map(
        this.$refs.mapContainer,
        defaultLayers.vector.normal.map,
        {
          center: this.normalizePoint(this.center) || { lat: 45.0732, lng: 7.6827 },
          zoom: this.zoom,
          pixelRatio: window.devicePixelRatio || 1
        }
      );

      this._behavior = new window.H.mapevents.Behavior(
        new window.H.mapevents.MapEvents(this._map)
      );

      this._ui = window.H.ui.UI.createDefault(this._map, defaultLayers);

      /**
       * Prima routeGroup, poi markerGroup:
       * così la linea resta sotto i marker.
       */
      this._routeGroup = new window.H.map.Group();
      this._markerGroup = new window.H.map.Group();

      this._map.addObject(this._routeGroup);
      this._map.addObject(this._markerGroup);

      window.addEventListener('resize', this.resizeMap);

      // All'inizializzazione disegniamo solo i marker: il calcolo rotta
      // viene guidato dal parent tramite l'evento 'ready' (o dal rendering
      // di una polyline salvata), evitando doppi calcoli in apertura.
      this.renderMarkersOnly();
      this.$emit('ready', this._map);
    },

    resizeMap() {
      if (this._map) {
        this._map.getViewPort().resize();
      }
    },

    renderMarkers() {
      const valid = this.renderMarkersOnly();

      if (!valid || valid.length === 0) {
        return;
      }

      /**
       * Routing solo se ho almeno 2 punti.
       */
      this.renderRoute(valid);

    
    },

    renderMarkersOnly() {
      if (!this._map || !this._markerGroup) {
        console.warn('[RouteEditorMapHere.renderMarkersOnly] Map or markerGroup not initialized');
        return [];
      }

      this._markerGroup.removeAll();

      if (this._routeGroup) {
        this._routeGroup.removeAll();
      }

      

      const valid = this.waypoints
        .map(wp => {
          const point = this.normalizePoint(wp);

          if (!point) return null;

          return {
            ...wp,
            lat: point.lat,
            lng: point.lng
          };
        })
        .filter(Boolean);


      if (valid.length === 0) {
        console.warn('[RouteEditorMapHere] No valid waypoints to display');
        return [];
      }

      valid.forEach((wp, index) => {
        const marker = new window.H.map.Marker({
          lat: wp.lat,
          lng: wp.lng
        });

        marker.setData(`
          <strong>${this.escapeHtml(wp.companyName || `Tappa ${index + 1}`)}</strong><br>
          ${this.escapeHtml(wp.address || '')}
        `);

        marker.addEventListener('tap', evt => {
          if (!this._ui) return;

          this._ui.getBubbles().forEach(bubble => {
            this._ui.removeBubble(bubble);
          });

          const bubble = new window.H.ui.InfoBubble(evt.target.getGeometry(), {
            content: evt.target.getData()
          });

          this._ui.addBubble(bubble);
        });

        this._markerGroup.addObject(marker);
      });

      /**
       * Prima centra sui marker, così anche se il routing fallisce
       * la mappa rimane comunque leggibile.
       */
      this.fitToCurrentObjects();

      return valid;
    },

    renderSavedPolyline(flexiblePolyline) {
      if (!this._map || !this._routeGroup || !this._markerGroup || !flexiblePolyline || !window.H) {
        return false;
      }

      try {
        this._routeGroup.removeAll();
        this._markerGroup.removeAll();

        const lineString = window.H.geo.LineString.fromFlexiblePolyline(flexiblePolyline);

        const routeLine = new window.H.map.Polyline(lineString, {
          style: {
            lineWidth: 5,
            strokeColor: 'rgba(37, 99, 235, 0.85)'
          }
        });

        this._routeGroup.addObject(routeLine);

        const pointCount = lineString.getPointCount();
        if (pointCount > 0) {
          const startPt = lineString.extractPoint(0);
          const endPt = lineString.extractPoint(pointCount - 1);

          this._markerGroup.addObject(new window.H.map.Marker({ lat: startPt.lat, lng: startPt.lng }));
          this._markerGroup.addObject(new window.H.map.Marker({ lat: endPt.lat, lng: endPt.lng }));
        }

        this.waypoints.slice(1, -1).forEach(wp => {
          const point = this.normalizePoint(wp);
          if (point) {
            this._markerGroup.addObject(new window.H.map.Marker({ lat: point.lat, lng: point.lng }));
          }
        });

        this.fitToCurrentObjects();

        return true;
      } catch (error) {
        console.error('[RouteEditorMapHere] renderSavedPolyline error:', error);
        return false;
      }
    },

    renderRoute(validWaypoints) {
      if (!this._map || !this._platform || !this._routeGroup) return;

      this._routeGroup.removeAll();

      if (!validWaypoints || validWaypoints.length < 2) {
        return;
      }

      if (this._routeRequestTimeout) {
        clearTimeout(this._routeRequestTimeout);
      }

      this._routeRequestTimeout = setTimeout(() => {
        this.calculateRoute(false);
      }, 250);
    },

    detectAdjacentDuplicates() {
      const groups = [];
      let current = null;

      for (let i = 1; i < this.waypoints.length - 1; i++) {
        const addr = this.normalizeAddress(this.waypoints[i].address);
        const prevAddr = this.normalizeAddress(this.waypoints[i - 1].address);
        const nextAddr = this.normalizeAddress(this.waypoints[i + 1].address);

        if (addr === prevAddr || addr === nextAddr) {
          if (!current || current.end !== i - 1) {
            current = { start: i, end: i };
          } else {
            current.end = i;
          }
        } else if (current && current.end === i - 1) {
          groups.push(current);
          current = null;
        }
      }

      if (current) groups.push(current);
      return groups;
    },

    normalizeAddress(addr) {
      return String(addr || '').toLowerCase().trim();
    },

    async calculateRoute(preserveView = false, optimize = this.optimizeRoute) {
      if (!this._map || !this._platform || !this._routeGroup) {
        console.warn('[RouteEditorMapHere.calculateRoute] Map, platform or routeGroup not initialized');
        return;
      }
      if (this.waypoints.length < 2) {
        console.warn('[RouteEditorMapHere.calculateRoute] Less than 2 waypoints');
        return;
      }

      const duplicates = this.detectAdjacentDuplicates();
      if (duplicates.length > 0) {
        this.$emit('adjacent-duplicates-detected', duplicates);
        return;
      }

      const validWaypoints = this.waypoints
        .map(wp => {
          const point = this.normalizePoint(wp);
          if (!point) return null;
          return { ...wp, lat: point.lat, lng: point.lng };
        })
        .filter(Boolean);


      if (validWaypoints.length < 2) {
        console.warn('[RouteEditorMapHere.calculateRoute] Not enough valid waypoints:', validWaypoints.length);
        return;
      }

      const requestId = ++this._routeRequestId;
      this._routeGroup.removeAll();

      try {
        if (!this.strategy?.calculateRoute) {
          throw new Error('Strategia di routing HERE non disponibile');
        }

        const result = await this.strategy.calculateRoute({
          waypoints: validWaypoints,
          avoidTolls: this.avoidTolls,
          avoidHighways: this.avoidHighways,
          avoidFerries: this.avoidFerries,
          optimize,
          vehicle: this.vehicle
        });

        if (requestId !== this._routeRequestId) return;

        this.renderRouteResult(result, preserveView);
        this.$emit('route-calculated', result);

        if (Array.isArray(result.optimizedWaypoints)) {
          this.$emit('waypoints-optimized', result.optimizedWaypoints);
        }
      } catch (error) {
        if (requestId !== this._routeRequestId) return;

        console.error('[RouteEditorMapHere] errore routing:', error);
        this.$emit('route-error', error);
      }
    },

    renderRouteResult(result, preserveView) {
      if (!this._routeGroup || !result?.route?.sections?.length) {
        throw new Error('HERE non ha restituito alcun percorso');
      }

      this._routeGroup.removeAll();

      result.route.sections.forEach(section => {
        if (!section.polyline) return;

        const lineString = window.H.geo.LineString.fromFlexiblePolyline(section.polyline);
        const routeLine = new window.H.map.Polyline(lineString, {
          style: {
            lineWidth: 5,
            strokeColor: 'rgba(37, 99, 235, 0.85)'
          }
        });

        this._routeGroup.addObject(routeLine);
      });

      if (!preserveView) {
        this.fitToCurrentObjects();
      }
    },

    clearRoute() {
      if (!this._map) return;

      if (this._routeGroup) {
        this._routeGroup.removeAll();
      }

      if (this._markerGroup) {
        this._markerGroup.removeAll();
      }
    },

    refresh() {
      if (!this._map) {
        console.warn('[RouteEditorMapHere.refresh] Map not initialized');
        return;
      }

      this.renderMarkers();
    },

    fitBounds() {
      this.fitToCurrentObjects();
    },

    fitToCurrentObjects() {
      if (!this._map) return;

      const routeBBox = this._routeGroup ? this._routeGroup.getBoundingBox() : null;
      const markerBBox = this._markerGroup ? this._markerGroup.getBoundingBox() : null;
      const bbox = routeBBox || markerBBox;

      if (!bbox) return;

      this._map.getViewModel().setLookAtData({
        bounds: bbox,
        padding: {
          top: 60,
          right: 60,
          bottom: 60,
          left: 60
        }
      });
    },

    normalizePoint(point) {
      if (!point) return null;

      const lat = this.toNumber(point.lat);
      const lng = this.toNumber(point.lng);


      if (!this.isValidLatLng(lat, lng)) {
        return null;
      }

      return { lat, lng };
    },

    toNumber(value) {
      if (value === null || value === undefined || value === '') {
        return NaN;
      }

      if (typeof value === 'string') {
        return Number(value.replace(',', '.'));
      }

      return Number(value);
    },

    isValidLatLng(lat, lng) {
      return Number.isFinite(lat)
        && Number.isFinite(lng)
        && lat >= -90
        && lat <= 90
        && lng >= -180
        && lng <= 180
        && !(lat === 0 && lng === 0);
    },

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }
};
</script>
